import type { CategorisedResponse, FullActivityItem } from '@customTypes/types';
import { ResponsCategory, Role } from '@customTypes/types';
import {
  AppShell,
  Avatar,
  Modal,
  ScrollArea,
  Space,
  Table,
  Text,
  Title
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { FormattedDate } from 'react-intl';

import { useGetCategorisedActivityesListQuery } from '@/features/admin-panel/api/getCategorisedActivitiesList/hook';
import { useCreateConversationMutation } from '@/features/chat/api';
import { useChatStore } from '@/features/chat/store/chatStore';
import { ArtistFullProfile } from '@/features/work/view/artist-full-profile/ArtistFullProfile';
import { ClubFullProfile } from '@/features/work/view/club-full-profile/ClubFullProfile';
import { resolveImagePath } from '@/shared/helper';
import { LBLinkButton } from '@/shared/ui-kit/components';

import { Board } from './components/board/Board';

const columnOrder: ResponsCategory[] = [
  ResponsCategory.pending,
  ResponsCategory.accepted,
  ResponsCategory.contractOngoing,
  ResponsCategory.artistRequestedEndDate,
  ResponsCategory.clubSetEndDate,
  ResponsCategory.completed,
  ResponsCategory.notSuccessful
];

export const ResponsCategoryLabelsRu: Record<ResponsCategory, string> = {
  [ResponsCategory.pending]: 'В ожидании',
  [ResponsCategory.accepted]: 'Принято',
  [ResponsCategory.contractOngoing]: 'Контракт в процессе',
  [ResponsCategory.clubSetEndDate]: 'Клуб указал дату окончания',
  [ResponsCategory.artistRequestedEndDate]: 'Артист попросил дату окончания',
  [ResponsCategory.completed]: 'Завершено',
  [ResponsCategory.notSuccessful]: 'Неудачно'
};

export const AdminKanban = () => {
  const userActivity = useGetCategorisedActivityesListQuery({
    options: {
      refetchInterval: 2000,
      refetchIntervalInBackground: true,
      retry: 3,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 3000)
    }
  });
  const [opened, { open, close }] = useDisclosure();
  const [profileOpened, { open: profileOpen, close: profileClose }] =
    useDisclosure();
  const [selectedProfile, setSelectedProfile] = useState<{
    profileType: Role;
    profileId: number;
  }>();
  const navigate = useNavigate({ from: '/admin/kanban' });
  const { chatProfile } = useChatStore();

  const getCurrentConversation = useCreateConversationMutation();
  const [currentActivitiItem, setCurrentActivityItem] =
    useState<FullActivityItem>();

  const onSelectItem = (activityItem: FullActivityItem) => {
    setCurrentActivityItem(activityItem);
    open();
  };

  const sortedData = useMemo<CategorisedResponse>(() => {
    if (!userActivity.data?.data) return {} as CategorisedResponse;
    return Object.fromEntries(
      columnOrder.map((key) => [
        ResponsCategoryLabelsRu[key],
        userActivity.data.data[key] ?? []
      ])
    ) as CategorisedResponse;
  }, [userActivity.data?.data]);

  const getConversation = async (
    senderProfileId: string,
    recipientProfileId: string,
    isDirect: boolean = true
  ) => {
    const conversation = await getCurrentConversation.mutateAsync({
      params: {
        senderProfileId,
        recipientProfileId,
        isDirect
      }
    });

    navigate({
      to: '/admin/chat/conversation/$conversationId',
      params: { conversationId: conversation.data.conversationId.toString() }
    });
  };

  const selectProfile = (profile: { profileType: Role; profileId: number }) => {
    setSelectedProfile(profile);
    profileOpen();
  };

  return (
    <AppShell.Main>
      <Modal
        withCloseButton
        opened={opened}
        size='auto'
        title={
          <Text fw='bold' fz={23}>
            Activity Item Details
          </Text>
        }
        onClose={close}
      >
        <Space h={40} />
        <ScrollArea type='never'>
          <Table highlightOnHover striped withColumnBorders withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Artist</Table.Th>
                <Table.Th>Club</Table.Th>
                <Table.Th>Contract</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td>
                  Name:{' '}
                  <LBLinkButton
                    onClick={() =>
                      selectProfile({
                        profileType: Role.artist,
                        profileId: currentActivitiItem?.artistProfileId!
                      })
                    }
                  >
                    <Text fw='bold'>{currentActivitiItem?.artistName}</Text>
                  </LBLinkButton>
                </Table.Td>
                <Table.Td>
                  Name:{' '}
                  <LBLinkButton
                    onClick={() =>
                      selectProfile({
                        profileType: Role.club,
                        profileId: currentActivitiItem?.clubProfileId!
                      })
                    }
                  >
                    <Text fw='bold'>{currentActivitiItem?.clubName}</Text>
                  </LBLinkButton>
                </Table.Td>
                <Table.Td>
                  Status: <Text fw='bold'>{currentActivitiItem?.status}</Text>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  Country:
                  <Text fw='bold'>{currentActivitiItem?.artistCountry}</Text>
                </Table.Td>
                <Table.Td>
                  Country:
                  <Text fw='bold'>{currentActivitiItem?.clubCountry}</Text>
                </Table.Td>
                <Table.Td>
                  Role: <Text fw='bold'>{currentActivitiItem?.jobRole}</Text>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  Email:
                  <Text fw='bold'>{currentActivitiItem?.artistEmail}</Text>
                </Table.Td>
                <Table.Td>
                  Email: <Text fw='bold'>{currentActivitiItem?.clubEmail}</Text>
                </Table.Td>
                <Table.Td>
                  Start at:
                  <Text fw='bold'>
                    {currentActivitiItem?.startDate ? (
                      <FormattedDate value={currentActivitiItem.startDate} />
                    ) : (
                      '-'
                    )}
                  </Text>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  Phone :
                  <Text fw='bold'>
                    {currentActivitiItem?.artistPhoneNumber}
                  </Text>
                </Table.Td>
                <Table.Td>
                  Club Phone:
                  <Text fw='bold'>
                    {currentActivitiItem?.clubPhoneNumber ?? '-'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  End at:
                  <Text fw='bold'>
                    <FormattedDate value={currentActivitiItem?.endDate} />
                  </Text>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  Chat :
                  <LBLinkButton
                    onClick={() =>
                      getConversation(
                        currentActivitiItem?.artistChatProfileId.toString()!,
                        chatProfile?.chatProfileId.toString()!
                      )
                    }
                  >
                    Go to chat
                  </LBLinkButton>
                </Table.Td>
                <Table.Td>
                  Chat:
                  <LBLinkButton
                    onClick={() =>
                      getConversation(
                        currentActivitiItem?.clubChatProfileId.toString()!,
                        chatProfile?.chatProfileId.toString()!
                      )
                    }
                  >
                    Go to chat
                  </LBLinkButton>
                </Table.Td>
                <Table.Td>
                  Chat:
                  <LBLinkButton
                    onClick={() =>
                      getConversation(
                        currentActivitiItem?.clubChatProfileId.toString()!,
                        currentActivitiItem?.artistChatProfileId.toString()!,
                        false
                      )
                    }
                  >
                    Go to common chat
                  </LBLinkButton>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  <Avatar
                    src={resolveImagePath(currentActivitiItem?.artistPhoto)}
                  />
                </Table.Td>

                <Table.Td>
                  <Avatar
                    src={resolveImagePath(currentActivitiItem?.clubPhoto)}
                  />
                </Table.Td>
                <Table.Td>
                  Artist Approved:
                  <Text fw='bold'>
                    {currentActivitiItem?.approvedByArtist ?? '-'}
                  </Text>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  Arrival Confirmed :
                  <Text fw='bold'>
                    {currentActivitiItem?.arrivalConfirmed ?? '-'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  Ticked Credit:
                  <Text fw='bold'>
                    {currentActivitiItem?.ticketCreditation ?? '-'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  Club Approved:
                  <Text fw='bold'>
                    {currentActivitiItem?.approvedByClub ?? '-'}
                  </Text>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td>
                  New end Date:
                  <Text fw='bold'>
                    {currentActivitiItem?.newEndDate ? (
                      <FormattedDate value={currentActivitiItem?.newEndDate} />
                    ) : (
                      '-'
                    )}
                  </Text>
                </Table.Td>
                <Table.Td>-</Table.Td>
                <Table.Td>-</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Modal>
      <Modal opened={profileOpened} onClose={profileClose}>
        {selectedProfile?.profileType === Role.club && (
          <ClubFullProfile clubId={selectedProfile?.profileId.toString()} />
        )}

        {selectedProfile?.profileType === Role.artist && (
          <ArtistFullProfile
            artistProfileId={selectedProfile?.profileId.toString()}
          />
        )}
      </Modal>
      <Title>Categorised Activities</Title>
      <Space h={40} />

      <ScrollArea h='80vh'>
        {sortedData && <Board data={sortedData} onSelectItem={onSelectItem} />}
      </ScrollArea>
    </AppShell.Main>
  );
};
